import { dayjs, type Dayjs } from "@av/config/extended-dayjs";

/**
 * Company timezone for the job-post close date. The recruiter picks a calendar
 * day; the post closes at 11:59 PM in this zone on that day. The browser sends
 * the calendar date (`closeDate`) and the server derives the 23:59 instant in
 * this zone, so the close time never depends on the browser's tzdata. Kept in
 * one place so a future per-company-timezone upgrade has a single seam to change.
 */
export const APPLICATION_CLOSE_TIME_ZONE = "America/Edmonton";

/**
 * Convert a recruiter-selected close date into the API `closeDate` calendar
 * date (`YYYY-MM-DD`). Returns `null` when no date is selected, which clears the
 * close date (and is what we send when the feature flag is off).
 */
export function toApplicationCloseDatePayload(date?: Dayjs | null): string | null {
  if (!date) {
    return null;
  }

  return date.format("YYYY-MM-DD");
}

/**
 * Convert a recruiter-selected close date into the API `applyBy` instant: 23:59
 * {@link APPLICATION_CLOSE_TIME_ZONE} on the selected day, as an ISO string, or
 * `null` when no date is selected. Demo mode stores it because its mock store
 * holds the API response shape.
 */
export function toApplicationCloseInstant(date?: Dayjs | null): string | null {
  if (!date) {
    return null;
  }

  return dayjs
    .tz(`${date.format("YYYY-MM-DD")} 23:59:00`, APPLICATION_CLOSE_TIME_ZONE)
    .toISOString();
}

/**
 * Convert the API `applyBy` instant into the date we show recruiters: the same
 * instant viewed in {@link APPLICATION_CLOSE_TIME_ZONE}, so the calendar day
 * matches the one the recruiter picked rather than the viewer's local day. The
 * inverse of {@link toApplicationCloseInstant}. Returns `undefined` when there
 * is no instant, or when the instant is invalid, so callers never render an
 * "Invalid Date"; callers apply their own display format.
 */
export function toApplicationCloseDate(instant?: Dayjs | null): Dayjs | undefined {
  if (!instant || !instant.isValid()) {
    return undefined;
  }

  return instant.tz(APPLICATION_CLOSE_TIME_ZONE);
}
